import { useState } from 'react';
import { Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import FeatherIcon from "feather-icons-react";
import Select from 'react-select';

const ColorConfigForm = ({
    mastersheet,
    property_id,
    building_name,
    onSave,
    onCancel,
    loading,
    existingConfig = null
}) => {
    const [configName, setConfigName] = useState(existingConfig?.name || '');
    const [colorRules, setColorRules] = useState(existingConfig?.colorRules || []);
    const [isActive, setIsActive] = useState(existingConfig?.isActive !== undefined ? existingConfig.isActive : true);
    const [errors, setErrors] = useState({});
    const [previewModal, setPreviewModal] = useState(false);
    const [activeTab, setActiveTab] = useState('field-rules');

    const operators = [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' },
        { value: 'greater_equal', label: 'Greater Than or Equal' },
        { value: 'less_equal', label: 'Less Than or Equal' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Does Not Contain' },
        { value: 'in', label: 'In List' },
        { value: 'not_in', label: 'Not In List' },
        { value: 'is_empty', label: 'Is Empty' },
        { value: 'is_not_empty', label: 'Is Not Empty' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' }
    ];

    const addFieldColorRule = () => {
        const newRule = {
            id: Date.now(),
            field: '',
            isActive: true,
            appliesTo: 'cell',
            priority: colorRules.length,
            ruleType: 'static',
            staticColor: {
                backgroundColor: '#ffffff',
                textColor: '#000000',
                borderColor: '#cccccc'
            },
            conditions: [],
            ranges: []
        };
        setColorRules([...colorRules, newRule]);
    };

    const removeFieldColorRule = (index) => {
        const updatedRules = colorRules.filter((_, i) => i !== index);
        setColorRules(updatedRules);
    };

    const updateFieldColorRule = (index, updatedRule) => {
        const updatedRules = [...colorRules];
        updatedRules[index] = { ...updatedRules[index], ...updatedRule };
        setColorRules(updatedRules);
    };

    const addCondition = (ruleIndex) => {
        const newCondition = {
            id: Date.now(),
            operator: 'equals',
            value: '',
            priority: 1,
            affectedFields: [],
            color: {
                backgroundColor: '#ffffff',
                textColor: '#000000',
                borderColor: '#cccccc'
            }
        };
        const updatedRules = [...colorRules];
        if (!updatedRules[ruleIndex].conditions) {
            updatedRules[ruleIndex].conditions = [];
        }
        updatedRules[ruleIndex].conditions.push(newCondition);
        setColorRules(updatedRules);
    };

    const addRange = (ruleIndex) => {
        const newRange = {
            id: Date.now(),
            min: 0,
            max: 100,
            color: {
                backgroundColor: '#ffffff',
                textColor: '#000000',
                borderColor: '#cccccc'
            }
        };
        const updatedRules = [...colorRules];
        if (!updatedRules[ruleIndex].ranges) {
            updatedRules[ruleIndex].ranges = [];
        }
        updatedRules[ruleIndex].ranges.push(newRange);
        setColorRules(updatedRules);
    };

    const removeCondition = (ruleIndex, conditionIndex) => {
        const updatedRules = [...colorRules];
        updatedRules[ruleIndex].conditions.splice(conditionIndex, 1);
        setColorRules(updatedRules);
    };

    const removeRange = (ruleIndex, rangeIndex) => {
        const updatedRules = [...colorRules];
        updatedRules[ruleIndex].ranges.splice(rangeIndex, 1);
        setColorRules(updatedRules);
    };

    const updateCondition = (ruleIndex, conditionIndex, updatedCondition) => {
        const updatedRules = [...colorRules];
        updatedRules[ruleIndex].conditions[conditionIndex] = {
            ...updatedRules[ruleIndex].conditions[conditionIndex],
            ...updatedCondition
        };
        setColorRules(updatedRules);
    };

    const updateRange = (ruleIndex, rangeIndex, updatedRange) => {
        const updatedRules = [...colorRules];
        updatedRules[ruleIndex].ranges[rangeIndex] = {
            ...updatedRules[ruleIndex].ranges[rangeIndex],
            ...updatedRange
        };
        setColorRules(updatedRules);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!configName.trim()) {
            newErrors.configName = 'Configuration name is required';
        }

        if (!property_id) {
            newErrors.property_id = 'Property ID is required';
        }

        colorRules.forEach((rule, index) => {
            if (!rule.field) {
                newErrors[`field_rule_${index}_field`] = 'Field is required for each rule';
            }

            if (rule.ruleType === 'static' && !rule.staticColor) {
                newErrors[`field_rule_${index}_staticColor`] = 'Static color is required for static rules';
            }

            if (rule.ruleType === 'conditional' && (!rule.conditions || rule.conditions.length === 0)) {
                newErrors[`field_rule_${index}_conditions`] = 'At least one condition is required for conditional rules';
            }

            if (rule.ruleType === 'range' && (!rule.ranges || rule.ranges.length === 0)) {
                newErrors[`field_rule_${index}_ranges`] = 'At least one range is required for range-based rules';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) {
            console.log(errors)
            return;
        }
        console.log(errors)

        const configData = {
            property_id,
            building_name,
            mastersheet_id: mastersheet?.id || mastersheet?._id,
            name: configName.trim(),
            colorRules,
            isActive
        };

        onSave(configData);
    };

    const getAvailableFields = () => {
        if (!mastersheet || !mastersheet.fields) {
            return [];
        }
        return mastersheet.fields.map(field => ({
            value: field.name || field.key,
            label: field.displayName || field.name || field.key,
            type: field.type || 'text'
        }));
    };

    const availableFields = getAvailableFields();

    return (
        <div className="advanced-color-config-form">
            <div className="btn-group mb-4">
                <Button
                    color="primary"
                    outline
                    active={activeTab === 'field-rules'}
                    onClick={() => setActiveTab('field-rules')}
                    className="d-flex align-items-center"
                    size="md"
                >
                    <FeatherIcon icon="layout" className="me-2" size={12} />
                    Field Color Rules
                </Button>
                <Button
                    color="primary"
                    outline
                    active={activeTab === 'settings'}
                    onClick={() => setActiveTab('settings')}
                    className="d-flex align-items-center"
                    size="md"
                >
                    <FeatherIcon icon="settings" className="me-2" size={12} />
                    Settings
                </Button>
            </div>
            <form onSubmit={handleSubmit}>
                {activeTab === 'settings' && (
                    <div className="tab-content">
                        <div className="mb-3">
                            <label className="form-label">Configuration Name *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.configName ? 'is-invalid' : ''}`}
                                value={configName}
                                onChange={(e) => setConfigName(e.target.value)}
                                placeholder="Enter color coding configuration name"
                                required
                            />
                            {errors.configName && (
                                <div className="invalid-feedback">{errors.configName}</div>
                            )}
                        </div>

                        <div className="mb-3">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="isActive"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="isActive">
                                    Active Configuration
                                </label>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-body">
                                <h6 className="card-title">Configuration Summary</h6>
                                <div className="row">
                                    <div className="col-md-6">
                                        <small className="text-muted">Field Color Rules:</small>
                                        <div className="fw-bold">{colorRules.length}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'field-rules' && (
                    <div className="tab-content">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="mb-1">Field Color Rules</h5>
                                <small className="text-muted">Define colors for specific fields based on their values</small>
                            </div>
                            <Button
                                type="button"
                                color="outline-primary"
                                size="sm"
                                onClick={addFieldColorRule}
                            >
                                <FeatherIcon icon="plus" className="me-1" size={12} />
                                Add Field Rule
                            </Button>
                        </div>

                        {colorRules.length === 0 ? (
                            <Alert color="info">
                                <FeatherIcon icon="info" className="me-1" size={12} />
                                No field color rules defined. Click "Add Field Rule" to create your first rule.
                            </Alert>
                        ) : (
                            <div className="field-rules-list">
                                {colorRules.map((rule, index) => (
                                    <FieldColorRuleCard
                                        key={rule.id || index}
                                        rule={rule}
                                        index={index}
                                        availableFields={availableFields}
                                        operators={operators}
                                        errors={errors}
                                        onUpdateRule={updateFieldColorRule}
                                        onRemoveRule={removeFieldColorRule}
                                        onAddCondition={addCondition}
                                        onRemoveCondition={removeCondition}
                                        onUpdateCondition={updateCondition}
                                        onAddRange={addRange}
                                        onRemoveRange={removeRange}
                                        onUpdateRange={updateRange}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="d-flex gap-2 mt-4 pt-3 border-top">
                    <Button
                        type="button"
                        color="outline-info"
                        onClick={() => setPreviewModal(true)}
                        disabled={loading}
                    >
                        <FeatherIcon icon="eye" className="me-1" size={12} />
                        Preview
                    </Button>
                    <div className="ms-auto d-flex gap-2">
                        <Button
                            type="submit"
                            color="primary"
                            disabled={loading || !configName.trim()}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <FeatherIcon icon="check-circle" className="me-1" size={12} />
                                    Save Configuration
                                </>
                            )}
                        </Button>
                        <Button
                            type="button"
                            color="secondary"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </form>

            <Modal isOpen={previewModal} toggle={() => setPreviewModal(false)} size="lg">
                <ModalHeader toggle={() => setPreviewModal(false)}>
                    Configuration Preview
                </ModalHeader>
                <ModalBody>
                    <ConfigurationPreview
                        configName={configName}
                        fieldRules={colorRules}
                        availableFields={availableFields}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setPreviewModal(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

const FieldColorRuleCard = ({
    rule,
    index,
    availableFields,
    operators,
    errors,
    onUpdateRule,
    onRemoveRule,
    onAddCondition,
    onRemoveCondition,
    onUpdateCondition,
    onAddRange,
    onRemoveRange,
    onUpdateRange
}) => {
    const fieldOptions = availableFields.map(field => ({
        value: field.value,
        label: field.label
    }));

    return (
        <div className="card mb-3 border-primary">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <h6 className="card-title mb-0">
                            <FeatherIcon icon="layout" className="me-2" size={12} />
                            Field Rule {index + 1}
                        </h6>
                    </div>
                    <div className="d-flex gap-2">
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id={`field_rule_${index}_active`}
                                checked={rule.isActive}
                                onChange={(e) => onUpdateRule(index, { isActive: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor={`field_rule_${index}_active`}>
                                Active
                            </label>
                        </div>
                        <Button
                            type="button"
                            color="outline-danger"
                            size="sm"
                            onClick={() => onRemoveRule(index)}
                        >
                            <FeatherIcon icon="minus" size={12} />
                        </Button>
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-4">
                        <label className="form-label">Target Field *</label>
                        <select
                            className={`form-select ${errors[`field_rule_${index}_field`] ? 'is-invalid' : ''}`}
                            value={rule.field}
                            onChange={(e) => onUpdateRule(index, { field: e.target.value })}
                            required
                        >
                            <option value="">Select a field</option>
                            {availableFields.map(field => (
                                <option key={field.value} value={field.value}>{field.label}</option>
                            ))}
                        </select>
                        {errors[`field_rule_${index}_field`] && (
                            <div className="invalid-feedback">{errors[`field_rule_${index}_field`]}</div>
                        )}
                    </div>

                    <div className="col-md-4">
                        <label className="form-label">Rule Type</label>
                        <select
                            className="form-select"
                            value={rule.ruleType}
                            onChange={(e) => onUpdateRule(index, { ruleType: e.target.value })}
                        >
                            <option value="static">Static Color</option>
                            <option value="conditional">Conditional</option>
                            <option value="range">Range Based</option>
                        </select>
                    </div>

                    <div className="col-md-4">
                        <label className="form-label">Applies To</label>
                        <select
                            className="form-select"
                            value={rule.appliesTo}
                            onChange={(e) => onUpdateRule(index, { appliesTo: e.target.value })}
                        >
                            <option value="cell">Cell Only</option>
                            <option value="row">Entire Row</option>
                        </select>
                    </div>
                </div>

                {rule.ruleType === 'static' && (
                    <div className="mb-3">
                        <label className="form-label">Static Styling</label>
                        <div className="row g-2">
                            <div className="col-md-4">
                                <label className="form-label small">Background</label>
                                <input
                                    type="color"
                                    className="form-control form-control-color"
                                    value={rule.staticColor?.backgroundColor || '#ffffff'}
                                    onChange={(e) => onUpdateRule(index, {
                                        staticColor: {
                                            ...rule.staticColor,
                                            backgroundColor: e.target.value
                                        }
                                    })}
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small">Text</label>
                                <input
                                    type="color"
                                    className="form-control form-control-color"
                                    value={rule.staticColor?.textColor || '#000000'}
                                    onChange={(e) => onUpdateRule(index, {
                                        staticColor: {
                                            ...rule.staticColor,
                                            textColor: e.target.value
                                        }
                                    })}
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small">Border</label>
                                <input
                                    type="color"
                                    className="form-control form-control-color"
                                    value={rule.staticColor?.borderColor || '#cccccc'}
                                    onChange={(e) => onUpdateRule(index, {
                                        staticColor: {
                                            ...rule.staticColor,
                                            borderColor: e.target.value
                                        }
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {rule.ruleType === 'conditional' && (
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label mb-0">Conditions</label>
                            <Button
                                type="button"
                                color="outline-secondary"
                                size="sm"
                                onClick={() => onAddCondition(index)}
                            >
                                <FeatherIcon icon="plus" size={12} />
                                Add Condition
                            </Button>
                        </div>

                        {rule.conditions && rule.conditions.map((condition, condIndex) => (
                            <div key={condition.id || condIndex} className="border rounded p-3 mb-2">
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-3">
                                        <label className="form-label small">Operator</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={condition.operator}
                                            onChange={(e) => onUpdateCondition(index, condIndex, { operator: e.target.value })}
                                        >
                                            {operators.map(op => (
                                                <option key={op.value} value={op.value}>{op.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small">Value</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={condition.value}
                                            onChange={(e) => onUpdateCondition(index, condIndex, { value: e.target.value })}
                                            placeholder="Enter value"
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small">Affected Fields</label>
                                        <Select
                                            isMulti
                                            options={fieldOptions}
                                            value={fieldOptions.filter(option => condition.affectedFields?.includes(option.value))}
                                            onChange={(selectedOptions) => {
                                                const selectedFields = selectedOptions ? selectedOptions.map(option => option.value) : [];
                                                onUpdateCondition(index, condIndex, { affectedFields: selectedFields });
                                            }}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder="Select fields..."
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '30px',
                                                    height: '30px',
                                                    fontSize: '0.75rem'
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 6px'
                                                }),
                                                indicatorsContainer: (base) => ({
                                                    ...base,
                                                    height: '30px'
                                                }),
                                                multiValue: (base) => ({
                                                    ...base,
                                                    fontSize: '0.7rem'
                                                })
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Background</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-color form-control-sm"
                                            value={condition.color?.backgroundColor || '#ffffff'}
                                            onChange={(e) => onUpdateCondition(index, condIndex, {
                                                color: {
                                                    ...condition.color,
                                                    backgroundColor: e.target.value
                                                }
                                            })}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Text</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-color form-control-sm"
                                            value={condition.color?.textColor || '#000000'}
                                            onChange={(e) => onUpdateCondition(index, condIndex, {
                                                color: {
                                                    ...condition.color,
                                                    textColor: e.target.value
                                                }
                                            })}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Border</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-color form-control-sm"
                                            value={condition.color?.borderColor || '#cccccc'}
                                            onChange={(e) => onUpdateCondition(index, condIndex, {
                                                color: {
                                                    ...condition.color,
                                                    borderColor: e.target.value
                                                }
                                            })}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <Button
                                            type="button"
                                            color="outline-danger"
                                            size="sm"
                                            onClick={() => onRemoveCondition(index, condIndex)}
                                        >
                                            <FeatherIcon icon="minus" size={12} />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {rule.ruleType === 'range' && (
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label mb-0">Ranges</label>
                            <Button
                                type="button"
                                color="outline-secondary"
                                size="sm"
                                onClick={() => onAddRange(index)}
                            >
                                <FeatherIcon icon="plus" size={12} />
                                Add Range
                            </Button>
                        </div>

                        {rule.ranges && rule.ranges.map((range, rangeIndex) => (
                            <div key={range.id || rangeIndex} className="border rounded p-3 mb-2">
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-3">
                                        <label className="form-label small">Min Value</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={range.min}
                                            onChange={(e) => onUpdateRange(index, rangeIndex, { min: parseFloat(e.target.value) })}
                                            placeholder="Min"
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small">Max Value</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={range.max}
                                            onChange={(e) => onUpdateRange(index, rangeIndex, { max: parseFloat(e.target.value) })}
                                            placeholder="Max"
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Background</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-color form-control-sm"
                                            value={range.color?.backgroundColor || '#ffffff'}
                                            onChange={(e) => onUpdateRange(index, rangeIndex, {
                                                color: {
                                                    ...range.color,
                                                    backgroundColor: e.target.value
                                                }
                                            })}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Text</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-color form-control-sm"
                                            value={range.color?.textColor || '#000000'}
                                            onChange={(e) => onUpdateRange(index, rangeIndex, {
                                                color: {
                                                    ...range.color,
                                                    textColor: e.target.value
                                                }
                                            })}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Border</label>
                                        <input
                                            type="color"
                                            className="form-control form-control-color form-control-sm"
                                            value={range.color?.borderColor || '#cccccc'}
                                            onChange={(e) => onUpdateRange(index, rangeIndex, {
                                                color: {
                                                    ...range.color,
                                                    borderColor: e.target.value
                                                }
                                            })}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <Button
                                            type="button"
                                            color="outline-danger"
                                            size="sm"
                                            onClick={() => onRemoveRange(index, rangeIndex)}
                                        >
                                            <FeatherIcon icon="minus" size={12} />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="row">
                    <div className="col-md-6">
                        <label className="form-label">Priority</label>
                        <input
                            type="number"
                            className="form-control"
                            value={rule.priority}
                            onChange={(e) => onUpdateRule(index, { priority: parseInt(e.target.value) })}
                            min="0"
                        />
                        <small className="form-text text-muted">Higher numbers = higher priority</small>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ConfigurationPreview = ({ configName, fieldRules, availableFields }) => {
    return (
        <div className="configuration-preview">
            <div className="mb-4">
                <h6>Configuration: {configName || 'Unnamed Configuration'}</h6>
            </div>

            {fieldRules.length > 0 && (
                <div className="mb-4">
                    <h6 className="text-primary">Field Color Rules ({fieldRules.length})</h6>
                    <div className="table-responsive">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Type</th>
                                    <th>Applies To</th>
                                    <th>Active</th>
                                    <th>Preview</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fieldRules.map((rule, index) => (
                                    <tr key={index}>
                                        <td>{rule.field}</td>
                                        <td>
                                            <span className="badge bg-secondary">{rule.ruleType}</span>
                                        </td>
                                        <td>{rule.appliesTo}</td>
                                        <td>
                                            <span className={`badge ${rule.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                {rule.isActive ? 'Yes' : 'No'}
                                            </span>
                                        </td>
                                        <td>
                                            {rule.ruleType === 'static' && rule.staticColor && (
                                                <div
                                                    className="preview-box"
                                                    style={{
                                                        backgroundColor: rule.staticColor.backgroundColor,
                                                        color: rule.staticColor.textColor,
                                                        borderColor: rule.staticColor.borderColor,
                                                        padding: '4px 8px',
                                                        borderRadius: '3px',
                                                        border: '1px solid',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Sample
                                                </div>
                                            )}
                                            {rule.ruleType === 'conditional' && rule.conditions && rule.conditions[0] && (
                                                <div
                                                    className="preview-box"
                                                    style={{
                                                        backgroundColor: rule.conditions[0].color.backgroundColor,
                                                        color: rule.conditions[0].color.textColor,
                                                        borderColor: rule.conditions[0].color.borderColor,
                                                        padding: '4px 8px',
                                                        borderRadius: '3px',
                                                        border: '1px solid',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Sample
                                                </div>
                                            )}
                                            {rule.ruleType === 'range' && rule.ranges && rule.ranges[0] && (
                                                <div
                                                    className="preview-box"
                                                    style={{
                                                        backgroundColor: rule.ranges[0].color.backgroundColor,
                                                        color: rule.ranges[0].color.textColor,
                                                        borderColor: rule.ranges[0].color.borderColor,
                                                        padding: '4px 8px',
                                                        borderRadius: '3px',
                                                        border: '1px solid',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Sample
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {fieldRules.length === 0 && (
                <Alert color="info">
                    No rules configured yet. Add some rules to see the preview.
                </Alert>
            )}
        </div>
    );
};

export { ColorConfigForm };